/**
 * Sponsored Content Configuration System
 *
 * Promo *eligibility* and *selection* (which promo wins a given placement,
 * priority/budget weighting, rotation) now live server-side in
 * POST /promos/select (see backend/src/controllers/promos.js selectPromo) so
 * that a served impression is authoritative and can be billed against a
 * partner's budget. When nothing is eligible, select returns no promo and
 * no serve token. This file only retains
 * client-side session/timing
 * bookkeeping that PromoContent and other pages still need:
 *
 * 1. Timing Controls
 *    - POPUP_DELAY: Milliseconds before showing popup promotions
 *    - POPUP_COOLDOWN: Minimum time between popup promotions
 *    - INTERSPERSED_PROMO_FREQUENCY: Number of profiles between promotions
 *    - SESSION_TIMEOUT_MS: Time of inactivity before session ends
 *
 * 2. Session Management
 *    - A session is defined as a continuous period of user activity
 *    - Session ends when:
 *      1. User closes the browser/tab
 *      2. User is inactive for SESSION_TIMEOUT_MS (default: 30 minutes)
 *      3. User explicitly logs out
 *    - Session data is stored in localStorage and includes last-shown
 *      promotion ids (used as the `excludeId` rotation hint sent to
 *      POST /promos/select) and last activity timestamp.
 *
 * 3. Page Targeting
 *    - INTERSPERSED_PROMO_PAGES / POPUP_PROMO_PAGES describe which pages
 *      participate in each placement type on the client side (e.g. deciding
 *      whether to render a PromoContent slot at all between profile cards).
 */

interface PromoConfig {
    // Timing settings
    POPUP_DELAY: number;
    POPUP_COOLDOWN: number;
    MOBILE_BREAKPOINT: number;
    SESSION_TIMEOUT_MS: number; // Time of inactivity before session ends

    // Promotion placement settings
    INTERSPERSED_PROMO_FREQUENCY: number;
    INTERSPERSED_PROMO_PAGES: string[];
    POPUP_PROMO_PAGES: string[];

    // Session tracking
    SESSION_KEYS: {
        LAST_POPUP_SHOWN: string;
        FEATURED_MEMBERS_VISIBILITY: string;
        DISCOVER_VIEW_MODE: string;
        LAST_INTERSPERSED_PROMO: string; // Track last shown interspersed promotion
        LAST_SIDEBAR_PROMO: string; // Track last shown sidebar promotion
        LAST_POPUP_PROMO: string; // Track last shown popup promotion
        LAST_ACTIVITY: string; // Track last user activity
    };

    // Helper functions
    updateSessionActivity: () => void;
    isSessionActive: () => boolean;
    clearSessionData: () => void;
}

// Sponsored content configuration settings
export const PROMO_CONFIG: PromoConfig = {
    // Timing settings
    POPUP_DELAY: 6000, // milliseconds
    POPUP_COOLDOWN: 24 * 60 * 60 * 1000, // 24 hours
    MOBILE_BREAKPOINT: 768, // pixels
    SESSION_TIMEOUT_MS: 30 * 60 * 1000, // 30 minutes

    // Promotion placement settings
    INTERSPERSED_PROMO_FREQUENCY: 12, // Show a promotion after every 12 profiles
    INTERSPERSED_PROMO_PAGES: ['discover', 'search', 'liked'],
    POPUP_PROMO_PAGES: ['discover', 'search', 'liked'],

    // Session tracking
    SESSION_KEYS: {
        LAST_POPUP_SHOWN: 'lastMobileOverlayShown',
        FEATURED_MEMBERS_VISIBILITY: 'featured_members_visibility',
        DISCOVER_VIEW_MODE: 'discover_view_mode',
        LAST_INTERSPERSED_PROMO: 'last_interspersed_promo',
        LAST_SIDEBAR_PROMO: 'last_sidebar_promo',
        LAST_POPUP_PROMO: 'last_popup_promo',
        LAST_ACTIVITY: 'last_activity'
    },

    // Helper functions
    updateSessionActivity: () => {
        localStorage.setItem(PROMO_CONFIG.SESSION_KEYS.LAST_ACTIVITY, Date.now().toString());
    },

    isSessionActive: () => {
        const lastActivity = localStorage.getItem(PROMO_CONFIG.SESSION_KEYS.LAST_ACTIVITY);
        if (!lastActivity) return false;

        const now = Date.now();
        const lastActivityTime = parseInt(lastActivity);
        return now - lastActivityTime <= PROMO_CONFIG.SESSION_TIMEOUT_MS;
    },

    clearSessionData: () => {
        // Clear all session-specific data
        localStorage.removeItem(PROMO_CONFIG.SESSION_KEYS.LAST_INTERSPERSED_PROMO);
        localStorage.removeItem(PROMO_CONFIG.SESSION_KEYS.LAST_SIDEBAR_PROMO);
        localStorage.removeItem(PROMO_CONFIG.SESSION_KEYS.LAST_POPUP_PROMO);
        localStorage.removeItem(PROMO_CONFIG.SESSION_KEYS.LAST_ACTIVITY);
    },
};

export const shouldShowInterspersedPromo = (
    pageName: string,
    index: number,
    listLength: number
): boolean => (
    PROMO_CONFIG.INTERSPERSED_PROMO_PAGES.includes(pageName) &&
    (index + 1) % PROMO_CONFIG.INTERSPERSED_PROMO_FREQUENCY === 0 &&
    index !== listLength - 1
);

// For backward compatibility
export const AD_CONFIG = PROMO_CONFIG;
